import { useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { getInitials } from '../utils/user';
import PageHeader from '../components/PageHeader';
import EditProfileModal from '../features/auth/components/EditProfileModal';
import { ACCOUNT_PAGE_LABELS as LABELS } from '../utils/labels';

interface AccountPageProps {
  session: Session;
  projectId: string | null;
}

export default function AccountPage({ session, projectId: _projectId }: AccountPageProps) {
  const { first_name = '', last_name = '' } = session.user.user_metadata ?? {};
  const initials = getInitials(session.user);
  const displayName = [first_name, last_name].filter(Boolean).join(' ') || null;
  const [editOpen, setEditOpen] = useState(false);

  const memberSinceDate = new Date(session.user.created_at).toLocaleDateString('nl-BE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />

      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-xl mx-auto space-y-6">
          <div>
            <h1 className="text-lg font-semibold mb-1">{LABELS.sectionTitle}</h1>
            <p className="text-sm text-text-secondary">{LABELS.sectionSubtitle}</p>
          </div>

          <div className="rounded-2xl border border-border bg-surface">
            <div className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary text-on-primary flex items-center justify-center text-xl font-semibold shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                {displayName && <p className="font-semibold mb-0 mt-0">{displayName}</p>}
                <p className="text-sm text-text-secondary mb-0 mt-0">{session.user.email}</p>
                <time
                  dateTime={session.user.created_at}
                  className="text-sm text-text-secondary mb-0 mt-0"
                >
                  {LABELS.memberSince} {memberSinceDate}
                </time>
              </div>
              <Tooltip title={LABELS.editProfile}>
                <IconButton
                  size="small"
                  onClick={() => setEditOpen(true)}
                  aria-label={LABELS.editProfile}
                  data-testid="edit-profile-button"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
      </main>

      {editOpen && <EditProfileModal session={session} onClose={() => setEditOpen(false)} />}
    </div>
  );
}
